import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import TextDateTime from "../../components/TextDateTime";
import { ProductReviewStatus } from "../../types/ProductReviewType";

interface OrderDetailItem {
  id: number;
  order_id: number;
  sale_order_id?: number; // Alias for order_id
  product_id: number;
  product_variant_id: number;
  item_name: string;
  item_image?: string;
  item_option_value_text?: string;
  order_code?: string;
  date_created?: number;
  shipping_status?: number;
}

interface ProductReviewWriteModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
  onSuccess?: (status: number) => void;
}

const ProductReviewWriteModal: React.FC<ProductReviewWriteModalProps> = ({
  isOpen,
  onClose,
  productId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [orderDetails, setOrderDetails] = useState<OrderDetailItem[]>([]);
  const [selectedOrderDetail, setSelectedOrderDetail] =
    useState<OrderDetailItem | null>(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [needsPurchase, setNeedsPurchase] = useState(false);

  // Fetch order details when modal opens
  useEffect(() => {
    if (isOpen && productId > 0) {
      fetchOrderDetails();
    } else if (isOpen) {
      setError("Product ID is required");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, productId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRating(0);
      setComment("");
      setSelectedOrderDetail(null);
      setError(null);
      setNeedsLogin(false);
      setNeedsPurchase(false);
      setOrderDetails([]);
    }
  }, [isOpen]);

  const fetchOrderDetails = async () => {
    setLoadingOrderDetails(true);
    setError(null);
    setNeedsLogin(false);
    setNeedsPurchase(false);

    try {
      const response = await fetch(
        `/productreview/orderdetailsajax?product_id=${productId}`
      );
      const result = await response.json();

      if (result.success) {
        if (result.items && result.items.length > 0) {
          // Normalize order_id/sale_order_id
          const normalizedItems = result.items.map((item: any) => ({
            ...item,
            sale_order_id: item.sale_order_id || item.order_id,
          }));
          setOrderDetails(normalizedItems);
          // Auto-select first item
          setSelectedOrderDetail(normalizedItems[0]);
        } else {
          setOrderDetails([]);
          setSelectedOrderDetail(null);
        }
      } else {
        // Check for specific error types
        if (result.error) {
          const errorMsg = Array.isArray(result.error)
            ? result.error.join(", ")
            : result.error;

          if (
            errorMsg.includes("error_login_required") ||
            errorMsg.includes("Unauthorized")
          ) {
            setNeedsLogin(true);
          } else if (
            errorMsg.includes("error_purchase_required") ||
            errorMsg.includes("not purchased")
          ) {
            setNeedsPurchase(true);
          } else {
            setError(errorMsg);
          }
        } else {
          setError("Failed to load order details");
        }
      }
    } catch (err) {
      console.error("Error fetching order details:", err);
      setError("Failed to load order details");
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOrderDetail) {
      setError("Order detail not found");
      return;
    }

    if (rating <= 0 || rating > 5) {
      setError("Please select a rating");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/productreview/addajax", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sale_order_id: selectedOrderDetail.order_id,
          sale_order_detail_id: selectedOrderDetail.id,
          product_id: selectedOrderDetail.product_id,
          product_variant_id: selectedOrderDetail.product_variant_id,
          rating: rating,
          comment: comment.trim(),
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Get review status from response
        const status =
          result.review?.status ||
          result.data?.status ||
          ProductReviewStatus.STATUS_PENDING;

        // Call success callback with status
        if (onSuccess) {
          onSuccess(status);
        }

        // Close modal immediately
        onClose();
      } else {
        // Display error from backend
        const errorMessage =
          result.error && Array.isArray(result.error)
            ? result.error.join(", ")
            : result.error || "Failed to submit review";
        setError(errorMessage);
      }
    } catch (err) {
      console.error("Error submitting review:", err);
      setError("Failed to submit review");
    } finally {
      setLoading(false);
    }
  };

  const StarRating: React.FC<{
    rating: number;
    onRatingChange: (rating: number) => void;
    label: string;
  }> = ({ rating, onRatingChange, label }) => {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        <div className="flex items-center space-x-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => onRatingChange(star)}
              className={`transition-colors ${
                star <= rating
                  ? "text-yellow-400"
                  : "text-gray-300 hover:text-yellow-300"
              }`}
            >
              <svg
                className="w-6 h-6"
                fill={star <= rating ? "currentColor" : "none"}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                />
              </svg>
            </button>
          ))}
          <span className="ml-2 text-sm text-gray-600">
            {rating > 0
              ? `${rating}/5`
              : t("account.reviews.modal.choose_stars")}
          </span>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("account.reviews.modal.product_review_title")}
      width="max-w-lg"
    >
      {loadingOrderDetails ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-2 text-gray-600">{t("account.reviews.loading")}</p>
        </div>
      ) : needsLogin ? (
        <div className="text-center py-8">
          <p className="text-gray-700 mb-4">
            {t("account.reviews.modal.login_required")}
          </p>
          <a
            href={`/login?return=${encodeURIComponent(
              `/product/redirect?id=${productId}`
            )}`}
            className="inline-block px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-dark transition-colors"
          >
            {t("account.reviews.modal.go_to_login")}
          </a>
        </div>
      ) : needsPurchase ? (
        <div className="text-center py-8">
          <p className="text-gray-700">
            {t("account.reviews.modal.purchase_required")}
          </p>
        </div>
      ) : error && !orderDetails.length ? (
        <div className="text-center py-8">
          <p className="text-red-600">{error}</p>
        </div>
      ) : orderDetails.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-700">
            {t("account.reviews.modal.no_purchase")}
          </p>
        </div>
      ) : selectedOrderDetail ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Order Detail Information Display */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            {selectedOrderDetail.order_id && (
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">
                  {t("account.orders.order_code")}:
                </span>
                <span className="text-sm text-gray-900">
                  {selectedOrderDetail.order_id}
                </span>
              </div>
            )}
            {selectedOrderDetail.item_option_value_text && (
              <div className="flex items-start space-x-2">
                <span className="text-sm font-medium text-gray-700">
                  {t("account.orders.product_options")}:
                </span>
                <span className="text-sm text-gray-900">
                  {selectedOrderDetail.item_option_value_text}
                </span>
              </div>
            )}
            {selectedOrderDetail.date_created && (
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">
                  {t("account.orders.placed_at")}:
                </span>
                <span className="text-sm text-gray-900">
                  <TextDateTime
                    format="DD/MM/YYYY"
                    ts={selectedOrderDetail.date_created}
                  />
                </span>
              </div>
            )}
          </div>

          {/* Rating */}
          <StarRating
            rating={rating}
            onRatingChange={setRating}
            label={t("account.reviews.modal.rating_product_label")}
          />

          {/* Comment */}
          <div>
            <label
              htmlFor="product-review-comment"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t("account.reviews.modal.comment_label")}
            </label>
            <textarea
              id="product-review-comment"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
              placeholder={t(
                "account.reviews.modal.comment_product_placeholder"
              )}
              maxLength={4000}
            />
            <div className="flex justify-end mt-1">
              <span className="text-xs text-gray-500">
                {comment.length}/4000
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              {t("account.reviews.modal.cancel_button")}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !selectedOrderDetail || rating <= 0}
            >
              {loading
                ? t("account.reviews.modal.processing")
                : t("account.reviews.modal.submit_button")}
            </button>
          </div>
        </form>
      ) : null}
    </Modal>
  );
};

export default ProductReviewWriteModal;
